import {
  AfterViewInit,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {PRIMARY_OUTLET, Router} from '@angular/router';
import {Utils} from '../../../shared/utils/utils';
import {Observable, Subscription} from 'rxjs';
import {Environment, SharedService} from '../../services/store/shared-store/shared-storage.service';
import {SharedStorageService} from '../../services/storage/shared-storage.service';
import {MatDialog} from '@angular/material/dialog';
import {SubmissionService} from '../../services/management/submission/submission.service';
import {FlowManagerService} from '../../../modules/flow/services/flow-manager.service';
import {FlowManagementComponent} from '../flow-management/flow-management.component';
import {FlowWebSocketsService} from '../../../modules/flow/services/flowBuilder/flow-web-sockets.service';
import {FlowBuilderService} from '../../../modules/flow/services/flowBuilder/flow-builder.service';
import {ZoomService} from '../../../modules/flow/services/flowBuilder/zoom.service';
import {RolesService} from '../../services/roles/roles.service';
import {FileClickEmitter} from '../../../shared/components/clickatell-tree/clickatell-tree.interface';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import {Store} from '@ngrx/store';
import {selectFlowModeState, selectFlowMetaData} from '../../state/selectors/flow.selector';
import {FlowModeState, FlowType} from "../../../shared/models/state/Flow";
import {flowMetaDataAction, flowModeAction} from "../../state";
import {FlowMetaData} from "../../../shared/models";
import {tap, take} from "rxjs/operators";
import { ResizeDirective } from '../../../shared/directives/resize/resize.directive';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { CustomMatExpansionToggleDirective } from '../../../shared/directives/custom-mat-expansion-toggle/custom-mat-expansion-toggle.directive';
import { NgClass, NgTemplateOutlet, AsyncPipe, Location } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSuffix } from '@angular/material/form-field';
import { ClickatellTreeComponent } from '../../../shared/components/clickatell-tree/clickatell-tree.component';

/** First URL segment of the flow editor route: {@code /flow/{env}/{entityType}/{entityId}/{flowRef}}. */
const FLOW_URL_PREFIX = 'flow';

/** Number of URL segments in a fully qualified flow editor route. */
const FLOW_URL_SEGMENT_COUNT = 5;

/** Index of the flow reference (flow id or flow path) within the flow editor route segments. */
const FLOW_URL_FLOW_REF_INDEX = 4;

@Component({
    selector: 'app-left-sidebar',
    templateUrl: './left-sidebar.component.html',
    styleUrls: ['./left-sidebar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [ResizeDirective, MatAccordion, MatExpansionPanel, CustomMatExpansionToggleDirective, MatExpansionPanelHeader, MatExpansionPanelTitle, NgClass, NgTemplateOutlet, MatTooltip, MatSuffix, ClickatellTreeComponent, MatMenuTrigger, MatMenu, MatMenuItem, AsyncPipe]
})
export class LeftSidebarComponent implements OnInit, DoCheck, OnDestroy, AfterViewInit {
  @Output() loadFlow: any = new EventEmitter();
  @Output() newFlow: any = new EventEmitter();
  @Output() loadRoute: any = new EventEmitter();
  @Output() openTemplateEvent: any = new EventEmitter();
  @Input() isTrashOpen: string;

  selectedForDuplication: string;
  multi = true;
  activeEnv: string;
  openAll = true;
  panelOpenState = false;
  configPanelExpanded = true;
  flowsPanelExpanded = true;
  flowDataSource: Observable<any[]>;
  canEditFlow: boolean;
  searchForValue: string;
  globalEnvironment: Environment;
  currentEnvironments: string;
  clientId: string;
  isUserInternal: boolean;
  isClientAdmin: boolean;

  @ViewChild(MatMenuTrigger)
  contextMenu: MatMenuTrigger;
  contextMenuPosition = { x: '0px', y: '0px' };
  private hasNotifications: boolean = false;
  private myCountSubscription: Subscription;

  public flowState$: Observable<String>;

  @ViewChild('parent') leftSideBar: ElementRef;
  @ViewChild('flowsPanel') flowsPanel: MatExpansionPanel;

  constructor(
    public router: Router,
    public flowManagerService: FlowManagerService,
    public rolesService: RolesService,
    public sharedService: SharedService,
    public zoomService: ZoomService,
    private utils: Utils,
    public dialog: MatDialog,
    public flowBuilderService: FlowBuilderService,
    public flowWebSocket: FlowWebSocketsService,
    private submissionService: SubmissionService,
    private store: Store,
    private sharedStorageService: SharedStorageService,
    private location: Location
  ) {}

  protected readonly FlowType = FlowType;

  ngOnInit() {
    this.isUserInternal = this.rolesService.isUserInternal();
    this.isClientAdmin = this.rolesService.isClientAdmin();
    
    if (!this.flowManagerService.flowsCache$) {
      this.flowManagerService.getFlows(true).pipe(take(1)).subscribe(() => {
        this.flowDataSource = this.flowManagerService.flowsCache$;
      });
    } else {
      this.flowDataSource = this.flowManagerService.flowsCache$;
    }
    
    // Don't reset flow type if already set in store
    this.store.select(selectFlowMetaData).pipe(
      take(1),
      tap(metaData => {
        if (!metaData || !metaData.flowType) {
          this.newFlowClick(FlowType.ChatFlow, true, false);
        }
      })
    ).subscribe();
    this.globalEnvironment = Object.assign({}, this.sharedService.getGlobalEnvironment());
    this.notifications();
    this.sharedService.setHasNotifications = true;
  }

  ngAfterViewInit(): void {
    this.flowState$ = this.store.select(selectFlowModeState);
  }

  ngOnDestroy(): void {
    this.myCountSubscription?.unsubscribe();
  }

  ngDoCheck(): void {
    const currentEnvString = JSON.stringify(this.globalEnvironment);
    const newEnvString = JSON.stringify(this.sharedService.getGlobalEnvironment());
    
    if (currentEnvString !== newEnvString) {
      this.fetchFlows(true);
      this.globalEnvironment = Object.assign({}, this.sharedService.getGlobalEnvironment());
    }

    if (this.sharedService.isNewFlowSaved() === true) {
      this.sharedService.setNewFlowSaved(false);
      this.fetchFlows(true);
    }
  }

  route(path: string) {
    // NB! Please leave this code block in for possible future usage
    // if (!this.sharedService.getIsFlowChanged()) {
    //   this.sharedService.isNodeSaved(false);
    //   this.setNewFlow();
    // }
    this.loadRoute.emit(path);
  }

  fetchFlows(reload: boolean) {
    this.flowDataSource = this.flowManagerService.getFlows(reload);
    this.flowDataSource.subscribe((flows) => {
      if (flows) {
        this.sharedService.clearFlows();
        for (const flow of flows) {
          // @ts-ignore
          if (flow.children) {
            // @ts-ignore
            for (const child of flow.children) {
              this.sharedService.addFlows(child);
            }
          } else {
            this.sharedService.addFlows(flow);
          }
        }
      }
    });
  }

  userCanEditFlow(): boolean {
    return this.rolesService.canEditFlows();
  }

  flowClicked(click: FileClickEmitter) {
    this.flowBuilderService.setCurrFlow(click);
    this.loadFlow.emit(click);
    this.setFlowMode(click.additionalData.flowType);
  }

  flowManagement(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dialog.open(FlowManagementComponent, {
      panelClass: 'flowManagementDialog',
      width: '80%',
      height: '65%',
    });
  }

  setNewFlow(flowType: FlowType = FlowType.ChatFlow) {
    const emit: FileClickEmitter = {
      name: this.sharedService.getNewFlowName(),
      path: this.sharedService.getNewFlowName(),
    };

    const flowMetaData: FlowMetaData = {
      flowId: '',
      flowName: '',
      directory: '',
      flowStatus: '',
      flowType: flowType
    };

    this.store.dispatch(flowMetaDataAction({ flowMetaData }));
    this.setFlowMode(flowType);

    this.loadFlow.emit(emit);
    this.sharedService.setIsFlowChanged(false);
  }

  private setFlowMode(flowType: FlowType = FlowType.ChatFlow) {
    switch (flowType) {
      case FlowType.ApiFlow:
        this.store.dispatch(flowModeAction({flowModeState: FlowModeState.API_FLOW_MODE}));
        break;
      case FlowType.ChatFlow:
      default:
        this.store.dispatch(flowModeAction({flowModeState: FlowModeState.FLOW_MODE}));
        break;
    }
  }

  newFlowClick(flowType: FlowType, validateUrl = false, openTemplateModal = false) {
    const flowPathName = this.sharedService.getFlowPathName();

    if (validateUrl && this.validateUrl() && this.rolesService.canEditFlows()) {
      return;
    }

    this.sharedService.setPendingFlowType(flowType);
    this.sharedService.setFlowName = this.sharedService.getNewFlowName();
    const currentURL = this.router.url;

    if (openTemplateModal) {
      this.setNewFlow(flowType);
      this.router.navigate([
        'flow',
        this.sharedService.getEnvironment(),
        this.sharedService.getEntityType(),
        this.sharedService.getEntityId(),
        '__NEW_FLOW__'
      ], { queryParams: { openTemplate: true } });
      this.openTemplateEvent.emit();
      return;
    }

    this.setNewFlow(flowType);
    this.sharedService.clearPendingFlowType();

    if (!currentURL.includes('__NEW_FLOW__') && !this.sharedService.getIsFlowChanged()) {
      this.setNewFlow(flowType);
      this.sharedService.clearPendingFlowType();
    } else if (currentURL.includes('__NEW_FLOW__') && !this.sharedService.getIsFlowChanged()) {
      this.setNewFlow(flowType);
      this.sharedService.clearPendingFlowType();
    }

    if (currentURL.includes('__NEW_FLOW__') && !this.sharedService.isFlowPristine && !this.sharedService.getIsFlowChanged()) {
      this.sharedService.isNodeSaved(false);
    } else if (this.sharedService.getIsFlowChanged()) {
      this.sharedService.isNodeSaved(true);
      this.sharedService.setFlowPathName(flowPathName);
    }
  }

  searchForChange(event: any) {
    this.searchForValue = event.value;
  }

  notifications(): void {
    if (!this.rolesService.canDeploy() || !this.rolesService.isReviewer()) {
      this.hasNotifications = false;
      return;
    }

    if (this.hasNotifications) {
      return;
    }

    this.myCountSubscription = this.submissionService
      .getSubmissionsCount()
      .subscribe((SubmissionsCount) => {
        if (this.rolesService.isReviewer() && this.rolesService.canDeploy()) {
          this.hasNotifications =
            SubmissionsCount['my_approvals_count'] > 0 ||
            SubmissionsCount['production_ready_count'] > 0;
          return;
        }

        if (this.rolesService.isReviewer()) {
          this.hasNotifications = SubmissionsCount['my_approvals_count'] > 0;
          return;
        }

        if (this.rolesService.canDeploy()) {
          this.hasNotifications = SubmissionsCount['production_ready_count'] > 0;
          return;
        }
      });
  }

  hasNotification(): boolean {
    return this.hasNotifications && this.sharedService.getHasNotifications;
  }

  /**
   * Determines whether the current URL already targets a specific, existing flow.
   *
   * <p>Used to suppress the automatic "new flow" reset performed on load, so that a deep-link of the
   * form {@code /flow/{env}/{entityType}/{entityId}/{flowRef}} resolves to the requested flow instead
   * of a blank canvas.</p>
   *
   * <p>The path is read from {@link Location} rather than {@code window.location.pathname} so the check
   * is independent of the deployed {@code base href}, and rather than {@code Router.url} because the
   * router's initial navigation may not have completed when this component initialises. It deliberately
   * does not compare against {@code SharedService} entity state — that state is only hydrated later by
   * {@code FlowComponent}, so it is still empty on a first (cold) page load.</p>
   *
   * @returns true when the active URL references an existing flow, false otherwise.
   */
  validateUrl(): boolean {
    const segments = this.router.parseUrl(this.location.path()).root.children[PRIMARY_OUTLET]?.segments ?? [];

    if (segments.length !== FLOW_URL_SEGMENT_COUNT || segments[0].path !== FLOW_URL_PREFIX) {
      return false;
    }

    const flowRef = segments[FLOW_URL_FLOW_REF_INDEX].path;

    return !!flowRef && flowRef !== this.sharedService.getNewFlowName();
  }

  onPanelOpened() {
    if (!this.flowsPanelExpanded) {
      this.flowsPanelExpanded = true;
    }
  }

  onPanelClosed() {
    if (this.flowsPanelExpanded) {
      this.flowsPanelExpanded = false;
    }
  }

}
